import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Info } from 'lucide-react';
import toast from '../../design-system/toast';
import { Spinner } from '../../design-system';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useTrialEligibility } from '../../hooks/useTrialEligibility';

const CreateOrganizationPage: React.FC = () => {
  const navigate = useNavigate();
  const { createOrganization, onboardingComplete, isLoading: orgLoading } = useOrganization();
  const { trialEligible } = useTrialEligibility();

  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgLoading && !onboardingComplete) {
      navigate('/onboarding/connect', { replace: true });
    }
  }, [navigate, onboardingComplete, orgLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();

    if (!trimmedName) {
      setError('Organization name is required');
      return;
    }

    if (trimmedName.length < 2) {
      setError('Organization name must be at least 2 characters');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await createOrganization(trimmedName);
      toast.success(`Organization "${trimmedName}" created`);
      navigate('/repositories');
    } catch (err: any) {
      console.error('Failed to create organization:', err);
      const errorMessage =
        err.data?.detail || err.message || 'Failed to create organization. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (orgLoading || !onboardingComplete) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Spinner size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-brand-primary/10 dark:bg-brand-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
          <Building2 className="w-5 h-5 text-brand-primary dark:text-brand-primaryDarkMode" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-ui-text-primary">
            Create Organization
          </h1>
          <p className="text-sm text-ui-text-tertiary mt-1">
            Create a new organization to manage applications and team members separately.
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4 sm:p-6 space-y-4"
      >
        {trialEligible === false && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800 dark:text-blue-200">
                This organization will not be eligible for a free trial. It will start on the free
                plan.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        <div>
          <label
            htmlFor="org-name"
            className="block text-sm font-medium text-ui-text-secondary mb-1"
          >
            Organization Name
          </label>
          <input
            type="text"
            id="org-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Corp"
            className="w-full px-4 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary focus:ring-2 focus:ring-brand-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSubmitting}
            autoFocus
          />
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-sm font-medium text-ui-text-secondary hover:bg-ui-background-tertiary rounded-lg transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !name.trim()}
            className="px-4 py-2 text-sm font-medium text-black bg-brand-primary hover:bg-brand-primaryDark disabled:opacity-50 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Spinner size={16} tone="current" />
                Creating...
              </>
            ) : (
              'Create Organization'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateOrganizationPage;
